import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, InputGroup, Spinner, Badge, Alert, Row, Col } from 'react-bootstrap';
import { Search, Zap, Star, AlertCircle } from 'lucide-react';
import { taskAPI } from '../../services/task';
import { shiftTaskAPI } from '../../services/shiftTask';
import { toast } from 'react-toastify';
import './AssignTaskModal.css';

const SEARCH_DEBOUNCE_MS = 350;
const BONUS_MAX = 200;
const NOTES_MAX_LENGTH = 500;
const TASK_FETCH_LIMIT = 50;
const BONUS_PRESETS = [0, 10, 20, 30, 50];

const WORK_STATUS_BADGE = {
  WORKING: { bg: 'success', text: 'Đang làm' },
  UPCOMING: { bg: 'info', text: 'Ca sắp tới' },
};

const AssignTaskModal = ({ show, onHide, shiftItem, onAssigned, onCreateTask }) => {
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [bonusPercent, setBonusPercent] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async (keyword) => {
    setLoadingTasks(true);
    try {
      const res = await taskAPI.getTasks({ search: keyword, is_active: true, task_type: 'SUB_TASK', limit: TASK_FETCH_LIMIT });
      setTasks(res?.data || []);
    } catch (err) {
      console.error('[AssignTaskModal] fetchTasks failed:', err);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setSelectedTask(null);
    setBonusPercent(0);
    setNotes('');
    setError(null);
    setSearch('');
    fetchTasks('');
  }, [show, fetchTasks]);

  useEffect(() => {
    const timer = setTimeout(() => fetchTasks(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, fetchTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTask) { setError('Vui lòng chọn task cần giao'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await shiftTaskAPI.assignTaskToShift({
        shift_id: shiftItem.shift_id,
        task_id: selectedTask.id,
        bonus_points_percentage: Number(bonusPercent) || 0,
        assignment_notes: notes.trim() || undefined,
      });
      toast.success(`Đã giao task "${selectedTask.name}" cho ${shiftItem.full_name}`);
      onAssigned?.(shiftItem.shift_id);
      onHide();
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || 'Giao task thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const shift = shiftItem?.shift;
  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  const workStatusBadge = WORK_STATUS_BADGE[shiftItem?.work_status] || WORK_STATUS_BADGE.WORKING;

  return (
    <>
    <Modal show={show} onHide={onHide} size="md" centered className="assign-task-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <Zap size={18} className="me-2 text-warning" />
          Giao Task Khẩn Cấp
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Employee banner — compact */}
        {shiftItem && (
          <div className="employee-banner mb-3">
            <div className="employee-banner-avatar">
              {shiftItem.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="employee-banner-info">
              <div className="employee-banner-name">{shiftItem.full_name}</div>
              <div className="employee-banner-meta">
                {shiftItem.role}
                <span className="separator">·</span>
                {shift?.branch_name}
                {shift && (
                  <><span className="separator">·</span>{formatTime(shift.start_time)} – {formatTime(shift.end_time)}</>
                )}
              </div>
            </div>
            <Badge bg={workStatusBadge.bg} className="working-badge">{workStatusBadge.text}</Badge>
          </div>
        )}

        <Form onSubmit={handleSubmit} id="assign-task-form">
          {error && (
            <Alert variant="danger" className="py-2 d-flex align-items-center gap-2 mb-2">
              <AlertCircle size={15} />
              <span className="mb-0">{error}</span>
            </Alert>
          )}

          {/* Task search & list */}
          <Form.Group className="mb-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Form.Label className="form-label-sm mb-0">
                Chọn Task <span className="text-danger">*</span>
              </Form.Label>
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                style={{ fontSize: '11px', padding: '2px 8px' }}
                onClick={() => onCreateTask?.({ onSaved: (newTask) => { setSelectedTask(newTask); fetchTasks(''); } })}
              >
                + Tạo task khẩn cấp
              </Button>
            </div>
            <InputGroup size="sm" className="mb-1">
              <InputGroup.Text><Search size={14} /></InputGroup.Text>
              <Form.Control
                size="sm"
                placeholder="Tìm tên task..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </InputGroup>
            <div className="task-list-box">
              {loadingTasks ? (
                <div className="task-list-placeholder">
                  <Spinner animation="border" size="sm" /><span>Đang tải...</span>
                </div>
              ) : tasks.length === 0 ? (
                <div className="task-list-placeholder">Không có task phù hợp</div>
              ) : (
                tasks.map((t) => (
                  <div
                    key={t.id}
                    className={`task-list-item ${selectedTask?.id === t.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTask(t)}
                    role="option"
                    aria-selected={selectedTask?.id === t.id}
                  >
                    <div className="task-list-item-name">
                      {t.name}
                      {t.is_urgent && (
                        <Badge bg="danger" className="ms-1" style={{ fontSize: '10px' }}>ĐỘT XUẤT</Badge>
                      )}
                    </div>
                    <div className="task-list-item-meta">
                      {t.required_for_checkout && <Badge bg="danger" className="me-1">Bắt buộc</Badge>}
                      {t.bonus_points_percentage > 0 && (
                        <Badge bg="warning" text="dark">+{t.bonus_points_percentage}%</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Form.Group>

          {/* Bonus % + Notes — 2 columns */}
          <Row className="g-2">
            <Col xs={5}>
              <Form.Group>
                <Form.Label className="form-label-sm">
                  <Star size={12} className="text-warning me-1" />
                  Thưởng tích luỹ
                </Form.Label>
                <div className="bonus-input-row mb-1">
                  <Form.Control
                    type="number"
                    size="sm"
                    min={0}
                    max={200}
                    step={5}
                    value={bonusPercent}
                    onChange={(e) => setBonusPercent(Math.min(BONUS_MAX, Math.max(0, Number(e.target.value))))}
                    className="bonus-input"
                  />
                  <span className="bonus-unit">%</span>
                </div>
                <div className="bonus-presets">
                  {BONUS_PRESETS.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`bonus-preset-btn ${bonusPercent === v ? 'active' : ''}`}
                      onClick={() => setBonusPercent(v)}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col xs={7}>
              <Form.Group>
                <Form.Label className="form-label-sm d-flex justify-content-between">
                  <span>Ghi chú</span>
                  <span className="text-muted">{notes.length}/{NOTES_MAX_LENGTH}</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  size="sm"
                  rows={3}
                  maxLength={NOTES_MAX_LENGTH}
                  placeholder="Mô tả yêu cầu cho nhân viên..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="notes-textarea"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button variant="light" size="sm" onClick={onHide} disabled={submitting}>
          Hủy
        </Button>
        <Button
          variant="warning"
          size="sm"
          type="submit"
          form="assign-task-form"
          disabled={submitting || !selectedTask}
          className="assign-submit-btn"
        >
          {submitting ? (
            <><Spinner animation="border" size="sm" className="me-1" />Đang giao...</>
          ) : (
            <><Zap size={14} className="me-1" />Giao Task</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>

    </>
  );
};

export default AssignTaskModal;
